/**
 * Taxa de timbru — formulă dinamică (linii + bifare), similar transport/taxe vamale.
 *
 * Amount is stored in stampDutyCurrency. At final entry price (RON) it is
 * converted with list FX rates when currency ≠ ron.
 *
 * Formula lines:
 * - per_kg: rate × weight
 * - per_unit: rate × quantity
 * Only enabled lines count toward the total.
 */

export const STAMP_DUTY_MODE_MANUAL = 'manual';
export const STAMP_DUTY_MODE_FORMULA = 'formula';

export const STAMP_DUTY_CALC_PER_KG = 'per_kg';
export const STAMP_DUTY_CALC_PER_UNIT = 'per_unit';

export const STAMP_DUTY_CURRENCY_OPTIONS = [
    { title: 'RON', value: 'ron' },
    { title: 'EUR', value: 'euro' },
    { title: 'USD', value: 'usd' },
    { title: 'PLN', value: 'pln' },
];

let stampDutyLineKeySeq = 0;

/**
 * @param {unknown} value
 * @returns {number}
 */
function parseNumericInput(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    let normalized = String(value).trim().replace(/\s/g, '');
    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');
    if (hasComma && hasDot) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
        normalized = normalized.replace(',', '.');
    } else if (hasDot && /^[+-]?\d{1,3}(\.\d{3})+$/.test(normalized)) {
        const integerPart = normalized.replace(/^[+-]/, '').split('.')[0];
        if (integerPart !== '0') {
            normalized = normalized.replace(/\./g, '');
        }
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * @param {number} value
 * @param {number} [decimals]
 * @returns {number}
 */
function roundMoney(value, decimals = 2) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
function formatNumericInputValue(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '';
    }
    return numeric.toLocaleString('ro-RO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    });
}

/**
 * @param {string} currency
 * @returns {string}
 */
export function normalizeStampDutyCurrency(currency) {
    const normalized = String(currency || '').trim().toLowerCase();
    if (normalized === 'eur' || normalized === 'euro') {
        return 'euro';
    }
    if (normalized === 'usd' || normalized === 'dolari' || normalized === 'dollar') {
        return 'usd';
    }
    if (normalized === 'pln') {
        return 'pln';
    }
    if (normalized === 'ron' || normalized === 'leu') {
        return 'ron';
    }
    return normalized || 'ron';
}

/**
 * @param {unknown} currency
 * @returns {string}
 */
export function stampDutyCurrencyLabel(currency) {
    const code = normalizeStampDutyCurrency(currency);
    if (code === 'euro') {
        return 'EUR';
    }
    if (code === 'usd') {
        return 'USD';
    }
    if (code === 'pln') {
        return 'PLN';
    }
    return 'RON';
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function parseWeightKg(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0 ? value : 0;
    }
    const match = String(value).trim().replace(/\s/g, ' ').match(/(\d+(?:[.,]\d+)?)/);
    if (!match) {
        return 0;
    }
    return parseNumericInput(match[1]);
}

/**
 * @param {string} currency
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {number}
 */
export function resolveStampDutyConverterToRon(currency, pricingSettings = {}) {
    const code = normalizeStampDutyCurrency(currency);
    if (code === 'ron') {
        return 1;
    }
    if (code === 'euro') {
        return parseNumericInput(pricingSettings.exchangeRateEur) || 0;
    }
    if (code === 'usd') {
        return parseNumericInput(pricingSettings.exchangeRateUsd) || 0;
    }
    if (code === 'pln') {
        return parseNumericInput(
            pricingSettings.exchangeRatePln ?? pricingSettings.exchangeRateRon,
        ) || 0;
    }
    return 0;
}

/**
 * @param {unknown} amount
 * @param {unknown} currency
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {number}
 */
export function getStampDutyAmountRon(amount, currency = 'ron', pricingSettings = {}) {
    const value = parseNumericInput(amount);
    if (!(value > 0)) {
        return 0;
    }
    const code = normalizeStampDutyCurrency(currency);
    if (code === 'ron') {
        return roundMoney(value, 2);
    }
    const fx = resolveStampDutyConverterToRon(code, pricingSettings);
    if (!(fx > 0)) {
        return 0;
    }
    return roundMoney(value * fx, 2);
}

/**
 * @returns {string}
 */
export function createStampDutyLineKey() {
    stampDutyLineKeySeq += 1;
    return `sd-${Date.now()}-${stampDutyLineKeySeq}`;
}

/**
 * @param {Partial<Record<string, unknown>>} [overrides]
 * @returns {Record<string, unknown>}
 */
export function createStampDutyLine(overrides = {}) {
    const calcType = overrides.calcType === STAMP_DUTY_CALC_PER_UNIT
        ? STAMP_DUTY_CALC_PER_UNIT
        : STAMP_DUTY_CALC_PER_KG;
    return {
        key: overrides.key || createStampDutyLineKey(),
        label: String(overrides.label ?? '').trim(),
        enabled: overrides.enabled !== false,
        calcType,
        rate: overrides.rate === null || overrides.rate === undefined
            ? ''
            : String(overrides.rate),
        rateCurrency: normalizeStampDutyCurrency(overrides.rateCurrency || 'ron'),
        weightKg: overrides.weightKg === null || overrides.weightKg === undefined
            ? ''
            : String(overrides.weightKg),
        weightIsManual: Boolean(overrides.weightIsManual),
        quantity: overrides.quantity === null || overrides.quantity === undefined
            || String(overrides.quantity).trim() === ''
            ? '1'
            : String(overrides.quantity),
    };
}

/**
 * Migrate legacy single-calc breakdown → one line.
 * @param {Record<string, unknown>} source
 * @returns {Record<string, unknown>[]}
 */
function linesFromLegacyBreakdown(source) {
    const hasLegacy = source.calcType
        || source.rate
        || source.weightKg
        || source.quantity;
    if (!hasLegacy) {
        return [];
    }
    return [createStampDutyLine({
        label: source.calcType === STAMP_DUTY_CALC_PER_UNIT
            ? 'Taxă pe bucată'
            : 'Taxă pe greutate',
        enabled: true,
        calcType: source.calcType === STAMP_DUTY_CALC_PER_UNIT
            ? STAMP_DUTY_CALC_PER_UNIT
            : STAMP_DUTY_CALC_PER_KG,
        rate: source.rate,
        rateCurrency: source.rateCurrency || 'ron',
        weightKg: source.weightKg,
        weightIsManual: Boolean(source.weightIsManual),
        quantity: source.quantity || '1',
    })];
}

/**
 * @param {Partial<Record<string, unknown>>} [overrides]
 * @returns {Record<string, unknown>}
 */
export function createDefaultStampDutyBreakdown(overrides = {}) {
    const defaultLines = [createStampDutyLine({
        label: 'Taxă pe greutate',
        enabled: true,
        calcType: STAMP_DUTY_CALC_PER_KG,
    })];
    const lines = Array.isArray(overrides.lines)
        ? overrides.lines.map((line) => createStampDutyLine(line || {}))
        : defaultLines;
    return {
        mode: STAMP_DUTY_MODE_MANUAL,
        amountIsManual: true,
        ...overrides,
        lines,
    };
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function normalizeStampDutyBreakdown(raw) {
    const source = raw && typeof raw === 'object' && !Array.isArray(raw)
        ? raw
        : {};
    const mode = source.mode === STAMP_DUTY_MODE_FORMULA
        ? STAMP_DUTY_MODE_FORMULA
        : STAMP_DUTY_MODE_MANUAL;
    let lines = Array.isArray(source.lines)
        ? source.lines.map((line) => createStampDutyLine(line || {}))
        : [];
    if (!lines.length) {
        lines = linesFromLegacyBreakdown(source);
    }
    if (!lines.length) {
        lines = [createStampDutyLine({
            label: 'Taxă pe greutate',
            enabled: true,
            calcType: STAMP_DUTY_CALC_PER_KG,
        })];
    }
    return {
        mode,
        amountIsManual: source.amountIsManual !== false,
        lines,
    };
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function cloneStampDutyBreakdown(raw) {
    return normalizeStampDutyBreakdown(raw);
}

/**
 * Prefill per-kg line weights from product when not manually overridden.
 * @param {Record<string, unknown>} breakdown
 * @param {{ weight?: unknown }} [context]
 * @returns {Record<string, unknown>}
 */
export function applyAutoStampDutyWeight(breakdown, context = {}) {
    const next = normalizeStampDutyBreakdown(breakdown);
    const kg = parseWeightKg(context.weight);
    const weightDisplay = kg > 0 ? formatNumericInputValue(kg, 4) : '';
    next.lines = next.lines.map((line) => {
        if (line.calcType !== STAMP_DUTY_CALC_PER_KG || line.weightIsManual) {
            return line;
        }
        return {
            ...line,
            weightKg: weightDisplay,
        };
    });
    return next;
}

/**
 * @param {Record<string, unknown>} line
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {{
 *   amountInCurrency: number,
 *   amountRon: number,
 *   currency: string,
 *   converter: number,
 * }}
 */
export function calculateStampDutyLine(line, pricingSettings = {}) {
    const normalized = createStampDutyLine(line || {});
    if (normalized.enabled === false) {
        return {
            amountInCurrency: 0,
            amountRon: 0,
            currency: normalized.rateCurrency,
            converter: 1,
        };
    }
    const rate = parseNumericInput(normalized.rate);
    const weightKg = parseNumericInput(normalized.weightKg);
    const quantity = Math.max(parseNumericInput(normalized.quantity), 0);
    const currency = normalizeStampDutyCurrency(normalized.rateCurrency);
    const converter = currency === 'ron'
        ? 1
        : resolveStampDutyConverterToRon(currency, pricingSettings);
    const amountInCurrency = normalized.calcType === STAMP_DUTY_CALC_PER_UNIT
        ? rate * (quantity > 0 ? quantity : 0)
        : rate * (weightKg > 0 ? weightKg : 0);
    const amountRon = roundMoney(
        amountInCurrency * (converter > 0 ? converter : 0),
        2,
    );
    return {
        amountInCurrency: roundMoney(amountInCurrency, 4),
        amountRon,
        currency,
        converter,
    };
}

/**
 * @param {unknown} rawBreakdown
 * @param {{
 *   weight?: unknown,
 *   pricingSettings?: Record<string, unknown>,
 * }} [context]
 * @returns {{
 *   amountInCurrency: number,
 *   amountRon: number,
 *   currency: string,
 *   lineAmounts: Array<{
 *     key: string,
 *     label: string,
 *     enabled: boolean,
 *     amountInCurrency: number,
 *     amountRon: number,
 *     currency: string,
 *   }>,
 *   baseAmount: number,
 * }}
 */
export function calculateStampDutyBreakdown(rawBreakdown, context = {}) {
    let breakdown = normalizeStampDutyBreakdown(rawBreakdown);
    breakdown = applyAutoStampDutyWeight(breakdown, context);
    const pricingSettings = context.pricingSettings || {};

    const lineAmounts = breakdown.lines.map((line) => {
        const computed = calculateStampDutyLine(line, pricingSettings);
        return {
            key: line.key,
            label: line.label,
            enabled: line.enabled !== false,
            amountInCurrency: computed.amountInCurrency,
            amountRon: computed.amountRon,
            currency: computed.currency,
        };
    });

    const enabled = lineAmounts.filter((line) => line.enabled && line.amountRon > 0);
    const currencies = [...new Set(enabled.map((line) => line.currency))];
    const amountRon = roundMoney(
        lineAmounts.reduce(
            (sum, line) => (line.enabled ? sum + line.amountRon : sum),
            0,
        ),
        2,
    );

    let currency = 'ron';
    let amountInCurrency = amountRon;
    if (currencies.length === 1) {
        currency = currencies[0];
        amountInCurrency = roundMoney(
            enabled.reduce((sum, line) => sum + line.amountInCurrency, 0),
            4,
        );
    }

    return {
        amountInCurrency,
        amountRon,
        currency,
        lineAmounts,
        baseAmount: amountInCurrency,
    };
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {boolean}
 */
export function syncStampDutyFromBreakdown(form, pricingSettings = {}) {
    let breakdown = normalizeStampDutyBreakdown(form.stampDutyBreakdown);
    breakdown = applyAutoStampDutyWeight(breakdown, { weight: form.weight });
    form.stampDutyBreakdown = breakdown;

    if (breakdown.mode !== STAMP_DUTY_MODE_FORMULA) {
        return false;
    }

    const computed = calculateStampDutyBreakdown(breakdown, {
        weight: form.weight,
        pricingSettings,
    });
    if (!breakdown.amountIsManual) {
        // Always store the RON equivalent on the form amount field.
        // Line rates may stay in EUR/USD/PLN; conversion uses list FX at sync
        // and again in calculateProductPricing via getStampDutyAmountRon.
        form.stampDutyCurrency = 'ron';
        form.stampDutyAmount = computed.amountRon > 0
            ? formatNumericInputValue(computed.amountRon, 2)
            : '0';
    }
    return true;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
export function serializeStampDutyBreakdown(raw) {
    const breakdown = normalizeStampDutyBreakdown(raw);
    return {
        mode: breakdown.mode,
        amountIsManual: Boolean(breakdown.amountIsManual),
        lines: breakdown.lines.map((line) => ({
            key: line.key,
            label: line.label,
            enabled: line.enabled !== false,
            calcType: line.calcType,
            rate: line.rate,
            rateCurrency: line.rateCurrency,
            weightKg: line.weightKg,
            weightIsManual: Boolean(line.weightIsManual),
            quantity: line.quantity,
        })),
    };
}
